import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const UserTransactions = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const role = localStorage.getItem("role"); // Get role from localStorage

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'short',   // Adds day like 'Mon', 'Tue'
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`https://api.happychd.com/api/user/${id}`);
        setUser(res.data);
      } catch (err) {
        console.error('Error fetching user:', err.message);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  if (loading) return <div className="p-6 text-center text-gray-600">Loading...</div>;
  if (!user) return <div className="p-6 text-center text-red-500">User not found</div>;

  const {
    name,
    userId,
    commission = 0,
    idPercentage = 0,
    referredBy,
    brokerReferrel,
    netBalance = 0,
    transactions = [],
    paymentStatus = [],
    balanceHistory = [],
    createdAt,
    updatedAt,
  } = user;

  // Utility
  const displayOrNA = (val) => (val !== null && val !== undefined && val !== '' ? val : 'N/A');
  const toNumber = (val) => Number(val || 0);

  // 🧮 Calculations
  const totalDeposit = transactions
    .filter((t) => t.type === 'deposit')
    .reduce((sum, t) => sum + toNumber(t.amount), 0);

  const totalWithdraw = transactions
    .filter((t) => t.type === 'withdraw')
    .reduce((sum, t) => sum + toNumber(t.amount), 0);

  const totalPaymentStatus = paymentStatus?.reduce((sum, p) => sum + toNumber(p.amount), 0);
  const totalBalance = balanceHistory?.reduce((sum, b) => sum + toNumber(b.amount), 0);

  const rawIdSettled =
    (totalWithdraw + totalBalance - totalDeposit) * (idPercentage / 100);

    const cmsnSettled =
    rawIdSettled < 0 ? Math.abs(rawIdSettled * (commission / 100)) : 0;

  const idSettled = (rawIdSettled + cmsnSettled);
  console.log(cmsnSettled)
  const netSettlement = totalPaymentStatus + idSettled;

  const deleteTransactions = async () => {
    const confirmed = window.confirm("⚠️ Are you sure you want to delete all transactions for this user?");
    if (!confirmed) return;

    try {
      const res = await axios.patch(`https://api.happychd.com/api/user/${id}/transactions`);
      alert("✅ Transactions deleted successfully!");
      // Refresh user data
      const refreshed = await axios.get(`https://api.happychd.com/api/user/${id}`);
      setUser(refreshed.data);
    } catch (error) {
      console.error("Error deleting transactions:", error);
      alert("❌ Failed to delete transactions.");
    }
  }

  return (
    <div className="p-6 max-w-auto mx-auto space-y-8">
      <div className="flex justify-between">

      <h2 className="text-xl font-semibold text-gray-800 mb-4">👤 User Details</h2>
      {role === "admin" && (
          <button
            onClick={deleteTransactions}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded shadow"
          >
            Delete All Transactions
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded shadow">
        <div><strong>Name:</strong> {displayOrNA(name)}</div>
        <div><strong>User ID:</strong> {displayOrNA(userId)}</div>
        <div><strong>Commission %:</strong> {displayOrNA(commission)}%</div>
        <div><strong>ID Percentage:</strong> {displayOrNA(idPercentage)}%</div>
        <div><strong>Guarantee:</strong> {displayOrNA(referredBy?.userId)}</div>
        <div><strong>Broker Referrel:</strong> {displayOrNA(brokerReferrel?.name)}</div>
        <div><strong>Net Balance:</strong> ₹{displayOrNA(netBalance)}</div>
        <div><strong>Created At:</strong> {formatDate(createdAt)}</div>
        <div><strong>Updated At:</strong> {formatDate(updatedAt)}</div>
      </div>

      <h2 className="text-xl font-semibold text-gray-800 mt-6">💼 Settlement Summary</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-white p-4 rounded shadow text-sm">
        <div><strong>User ID:</strong> {displayOrNA(userId)}</div>
        <div><strong>Commission %:</strong> {displayOrNA(commission)}%</div>
        <div><strong>ID Percentage:</strong> {displayOrNA(idPercentage)}%</div>
        <div><strong>Payment Status:</strong> ₹{totalPaymentStatus.toFixed(2)}</div>
        <div><strong>Deposit Total:</strong> ₹{totalDeposit.toFixed(2)}</div>
        <div><strong>Withdraw Total:</strong> ₹{totalWithdraw.toFixed(2)}</div>
        <div><strong>Balance Total:</strong> ₹{totalBalance.toFixed(2)}</div>
        <div><strong>Raw ID Settled:</strong> ₹{rawIdSettled.toFixed(2)}</div>
        <div><strong>Cmsn Settled:</strong> ₹{Math.abs(cmsnSettled).toFixed(2)}</div>
        <div><strong>ID Settled:</strong> ₹{idSettled.toFixed(2)}</div>
        <div><strong>Net Settlement:</strong> ₹{netSettlement.toFixed(2)}</div>
        <div><strong>ID:</strong> {userId}</div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">📄 Transaction History</h2>
          {transactions.length === 0 ? (
            <div className="text-center text-gray-600">No transactions found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Type</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Method</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <tr 
                      key={idx} 
                      className={`border-t ${
                        tx.type === 'deposit' 
                          ? 'bg-green-50 hover:bg-green-100' 
                          : 'bg-red-50 hover:bg-red-100'
                      } transition-colors duration-200`}
                    >
                      <td className="p-3 capitalize text-sm">{tx.type || 'N/A'}</td>
                      <td className="p-3 text-sm font-bold">₹{tx.amount || 'N/A'}</td>
                      <td className="p-3 uppercase text-sm">{tx.paymentMethod || 'N/A'}</td>
                      <td className="p-3 text-sm">{formatDate(tx.date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
    </div>
  );
};

export default UserTransactions;
